/**
 * Admin User Service
 * Handles admin user data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockAdminUsers, generateAdminId } from '../mock/mockAdminUsers';
import { delay } from '../mock/mockData';
import type {
  AdminUser,
  AdminUserFilter,
  PaginatedAdminUsers,
  AdminUserRole,
} from '../../types/adminUser';
import { generateId } from '../mock/mockData';
import { parseDate, formatDate, formatDateTime } from '../../utils/dateUtils';

class AdminUserService {

  /**
   * Convert internal AdminUserRole to API role format
   */
  private static convertRoleToApi(internalRole: AdminUserRole): string {
    switch (internalRole) {
      case 'super_admin':
        return 'Administrator';
      case 'support':
        return 'Support';
      case 'investigator':
        return 'Investigator';
      default:
        return 'Support'; // default fallback
    }
  }

  /**
   * Convert API admin user format to internal AdminUser format
   */
  private static convertAdminUserFromApi(apiUser: any): AdminUser {
    // Map API role to internal AdminUserRole
    let role: AdminUserRole = 'support'; // default
    const apiRole = apiUser.role.toLowerCase();
    if (apiRole.includes('admin')) {
      role = 'super_admin';
    } else if (apiRole.includes('support')) {
      role = 'support';
    } else if (apiRole.includes('investigator')) {
      role = 'investigator';
    }

    return {
      id: apiUser.id,
      adminId: apiUser.adminId,
      name: apiUser.name || (apiUser.firstName && apiUser.lastName
        ? `${apiUser.firstName} ${apiUser.lastName}`
        : apiUser.firstName || apiUser.lastName || 'Unknown User'),
      firstName: apiUser.firstName,
      lastName: apiUser.lastName,
      email: apiUser.email,
      avatar: apiUser.avatarUrl,
      role,
      status: apiUser.enabled ? 'active' : 'disabled',
      lastLogin: apiUser.lastLoginAt ? (parseDate(apiUser.lastLoginAt) ?? undefined) : undefined,
      lastLoginText: apiUser.lastLoginAt ? formatDateTime(apiUser.lastLoginAt) : '',
      createdAt: apiUser.createdAt ? (parseDate(apiUser.createdAt) ?? undefined) : undefined,
      createdAtText: apiUser.createdAt ? formatDate(apiUser.createdAt) : '',
    };
  }

  /**
   * Get paginated admin users with optional filters
   */
  static async getAdminUsers(
    page: number = 1,
    pageSize: number = 10,
    filter?: AdminUserFilter
  ): Promise<PaginatedAdminUsers> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredUsers = [...mockAdminUsers];

      // Apply role filter
      if (filter?.role && filter.role !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.role === filter.role
        );
      }

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.status === filter.status
        );
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.name?.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.adminId.toLowerCase().includes(searchLower)
        );
      }

      const total = filteredUsers.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredUsers.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      // Add optional filters
      if (filter?.role && filter.role !== 'all') {
        params.role = AdminUserService.convertRoleToApi(filter.role);
      }
      if (filter?.status && filter.status !== 'all') {
        params.isEnabled = filter.status === 'active' ? 'true' : 'false';
      }
      if (filter?.search) {
        params.search = filter.search;
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: {
          total: number;
          data: AdminUser[];
        };
        message?: string;
      }>('/admin/admin-users', { params });

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch admin users');
      }

      // Convert API response to expected format
      return {
        data: response.data.data.map(AdminUserService.convertAdminUserFromApi),
        total: response.data.total,
        page,
        pageSize,
        totalPages: Math.ceil(response.data.total / pageSize),
      };
    }
  }

  /**
   * Get admin user by ID
   */
  static async getAdminUserById(id: string): Promise<AdminUser | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return (
        mockAdminUsers.find(
          (user) =>
            user.id === id ||
            user.adminId === id
        ) || null
      );
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: AdminUser;
        message?: string;
      }>(`/admin/admin-users/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch admin user');
      }

      return response.data ? AdminUserService.convertAdminUserFromApi(response.data) : null;
    }
  }

  /**
   * Create a new admin user
   */
  static async createAdminUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: AdminUserRole;
  }): Promise<AdminUser | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      const newAdmin: AdminUser = {
        id: generateId(),
        adminId: generateAdminId(mockAdminUsers.length + 1),
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        role: data.role,
        status: 'active',
        createdAt: new Date(),
        createdAtText: formatDate(new Date()),
        lastLogin: undefined,
        lastLoginText: 'Never',
      };

      mockAdminUsers.push(newAdmin);
      return newAdmin;
    } else {
      const requestData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        role: AdminUserService.convertRoleToApi(data.role),
      };

      const response = await apiService.post<{
        isSuccess: boolean;
        data: AdminUser;
        message?: string;
      }>('/admin/admin-users', requestData);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to create admin user');
      }

      return response.data ? AdminUserService.convertAdminUserFromApi(response.data) : null;
    }
  }

  /**
   * Update an admin user
   */
  static async updateAdminUser(
    id: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      role?: AdminUserRole;
      password?: string;
    }
  ): Promise<AdminUser> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      const userIndex = mockAdminUsers.findIndex(
        (u) => u.id === id || u.adminId === id
      );

      if (userIndex === -1) {
        throw new Error('Admin user not found');
      }

      const updatedUser: AdminUser = {
        ...mockAdminUsers[userIndex],
        ...data,
        name: data.firstName && data.lastName
          ? `${data.firstName} ${data.lastName}`.trim()
          : data.firstName || data.lastName || mockAdminUsers[userIndex].name,
      };

      mockAdminUsers[userIndex] = updatedUser;
      return updatedUser;
    } else {
      const requestData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        role: data.role ? AdminUserService.convertRoleToApi(data.role) : undefined,
        password: data.password,
      };

      // Remove undefined values
      Object.keys(requestData).forEach(key => {
        if (requestData[key as keyof typeof requestData] === undefined) {
          delete requestData[key as keyof typeof requestData];
        }
      });

      const response = await apiService.put<{
        isSuccess: boolean;
        data: AdminUser;
        message?: string;
      }>(`/admin/admin-users/${id}`, requestData);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to update admin user');
      }

      return AdminUserService.convertAdminUserFromApi(response.data);
    }
  }

  /**
   * Change current user's password
   */
  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // In mock mode, just simulate success
      return;
    } else {
      const response = await apiService.put<any>('/admin/admin-users/me/password', data);

      if (response === undefined || response === null || response.isSuccess === false) {
        throw new Error(response?.message || 'Failed to change password');
      }
    }
  }

  /**
   * Delete an admin user
   */
  static async deleteAdminUser(id: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      const userIndex = mockAdminUsers.findIndex(
        (u) => u.id === id || u.adminId === id
      );

      if (userIndex === -1) {
        throw new Error('Admin user not found');
      }

      mockAdminUsers.splice(userIndex, 1);
    } else {
      try {
        await apiService.delete<void>(`/admin/admin-users/${id}`);
        // 204 No Content means success
      } catch (error: any) {
        throw new Error(error.message || 'Failed to delete admin user');
      }
    }
  }
}

export { AdminUserService };
